/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";

// Custom hook for managing quotes data
const useQuotesData = (apiQuotesData, selectedFuel = "electricity") => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate month options from 1 to 60
  const generateMonthOptions = () => {
    const options = [{ value: "", label: "All" }];
    for (let i = 1; i <= 60; i++) {
      options.push({
        value: i.toString(),
        label: `${i} Month${i > 1 ? "s" : ""}`,
      });
    }
    return options;
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  // Transform API data to match UI format
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((quote) => {
      // Parse HTML features to array
      const featuresHtml = quote.features || "";
      const featuresArray = featuresHtml
        .replace(/<ul>|<\/ul>/g, "")
        .split("<li>")
        .filter((item) => item.trim())
        .map((item) => item.replace(/<\/li>/g, "").trim());

      const featuresNotes = quote.featuresNotes || "";
      const cleanFeaturesNotes = stripHtmlTags(featuresNotes);

      const principleTerms = quote.principleTerms || "";
      const cleanPrincipleTerms = stripHtmlTags(principleTerms);
      const estimatedTotalCost =
        (quote?.yearlyCost / 12) * quote.contractLength;
      const estimatedMonthlyCost = quote?.yearlyCost / 12 || null;
      const extimatedAnnualCost =
        quote?.contractLength >= 13 ? quote?.yearlyCost : null;

      return {
        id: quote?.id,
        contractLength: quote?.contractLength, // <-- add this for filtering
        months: quote?.contractLength, // keep as months for dropdown
        type: quote?.tariffName,
        fuelType: selectedFuel === "electricity" ? "Electricity" : "Gas",
        pricePerKwh1: `${quote?.breakDown?.pricePerKwh1}p per kWh`,
        pricePerKwh2: `${quote?.breakDown?.pricePerKwh2}p per kWh`,
        meterType: quote?.breakDown?.meterType,
        estimatedTotalCost:
          estimatedTotalCost != null
            ? Number(estimatedTotalCost)?.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "",
        estimatedMonthlyCost:
          estimatedMonthlyCost != null
            ? Number(estimatedMonthlyCost)?.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "",
        standingCharge: `${quote?.breakDown?.standingCharge}p`,
        extimatedAnnualCost:
          extimatedAnnualCost != null
            ? Number(extimatedAnnualCost)?.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "",
        unitRate: `${quote?.breakDown?.unitRate}p`,

        features: featuresArray,
        terms: cleanFeaturesNotes,
        exclusions: cleanPrincipleTerms?.substring(0, 200) + "...", // Truncate for display
        annualConsumption: quote?.annualConsumption,
        annualConsumptionNight: quote?.annualConsumptionNight || 0,
      };
    });
  };

  // Fetch quotes data
  const fetchQuotes = async () => {
    try {
      setLoading(true);

      if (apiQuotesData) {
        // Use API data if available
        const transformedQuotes = transformApiData(apiQuotesData);
        setQuotes(transformedQuotes);
        setError(null);
      }
    } catch (err) {
      setError("Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [apiQuotesData]);

  // Filter quotes based on selected filter
  const filterQuotes = useCallback(
    (selectedFilter) => {
      // If no filter, show all
      if (selectedFilter === "") {
        return quotes;
      }

      // If 'monthly', show all quotes so dropdown can filter by contractLength
      if (selectedFilter === "monthly") {
        return quotes;
      }

      // If a number (from dropdown or fixed filter), filter by contractLength
      if (!isNaN(selectedFilter) && selectedFilter !== "") {
        return quotes.filter(
          (quote) => String(quote.contractLength) === String(selectedFilter),
        );
      }
      return quotes;
    },
    [quotes],
  );

  return {
    quotes,
    loading,
    error,
    monthOptions: generateMonthOptions(),
    filterQuotes,
    refetchQuotes: fetchQuotes,
  };
};

export default useQuotesData;
